import { createSelector } from 'reselect'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'utils/emptyConstants'
import { arrayOfObjectsToObject, arrayOfObjectsToObjectsOfArraysByKey } from 'utils/helpers'

import { selectActualCompanyName } from 'containers/App/selectors'
import { LEVEL_VALUES } from 'containers/AwarenessCenter/constants'
import { FILE_TYPE_OPTIONS, hasSupportedAttachmentFormat } from 'containers/ScenarioForm/constants'

import { SENDING_VALUES } from '../constants'
import {
  selectIsTournament,
  selectParticipants,
  selectSelectedDelivery,
  selectSelectedScenarios,
  selectSendingType,
} from '../selectors'

import {
  CREATED_BY_OPTIONS,
  DEFAULT_CATEGORY_ID,
  MAP_SCENARIOS_TYPE_OPTIONS_BY_DELIVERY,
  TYPE_LABELS,
  TYPE_VALUES,
} from './constants'

export const TEMPLATE_SELECTION_CORRECTION_MESSAGE =
  'Remove templates until the selection uses one attack vector and, for attachments, one format.'
const getTemplateSettingsLabel = ({ type, filetype }) =>
  type === TYPE_VALUES.ATTACHMENT
    ? `Attachment · ${FILE_TYPE_OPTIONS.find(
        ({ value }) => value === filetype
      )?.label.toUpperCase()}`
    : TYPE_LABELS[type]

export const getSelectedTemplateSettings = (selectedScenarios) => {
  if (!selectedScenarios?.length) return null

  const { type, filetype } = selectedScenarios[0]
  const selectedFiletype = type === TYPE_VALUES.ATTACHMENT ? filetype : null
  const isValid = selectedScenarios.every(
    ({ type: currentType, filetype: currentFiletype }) =>
      Number.isInteger(currentType) &&
      hasSupportedAttachmentFormat({ type: currentType, filetype: currentFiletype }) &&
      currentType === type &&
      (currentType === TYPE_VALUES.ATTACHMENT ? currentFiletype : null) === selectedFiletype
  )

  return isValid
    ? { type, filetype: selectedFiletype, isValid: true }
    : { type: null, filetype: null, isValid: false }
}

export const selectError = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.error
export const selectScenariosLoader = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.scenariosLoader
export const selectSearch = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.search
export const selectSortOption = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.sortOption
export const selectLevelFilter = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.levelFilter
export const selectTypeFilter = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.typeFilter
export const selectCreatedByFilter = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.createdByFilter
export const selectLocales = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.locales
export const selectIsVisited = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.isVisited
export const selectCreatedByOptions = (state) => {
  const companyName = selectActualCompanyName(state)
  return [...CREATED_BY_OPTIONS, { value: 4, label: companyName }]
}
// Two Sending Type allowed (random, level)
export const selectSelectedScenariosByLevels = createSelector(
  selectSendingType,
  selectSelectedScenarios,
  selectParticipants,
  (sendingType, selectedScenarios, participants) => {
    if (sendingType === SENDING_VALUES.RANDOMIZED) {
      return [
        {
          level: LEVEL_VALUES.ALL_LEVELS,
          scenarios: selectedScenarios,
          participants: participants.meta.count,
        },
      ]
    } else if (sendingType === SENDING_VALUES.LEVELED) {
      const scenariosByLevels = arrayOfObjectsToObjectsOfArraysByKey(selectedScenarios, 'level')

      const allParticipatedUniqueLevels = [
        ...new Set([
          ...Object.keys(scenariosByLevels),
          ...Object.keys(participants.meta.awarenessLevels).filter(
            (level) => participants.meta.awarenessLevels[level]
          ),
        ]),
      ]

      return allParticipatedUniqueLevels.sort().map((level) => ({
        level: Number(level),
        scenarios: scenariosByLevels[level] || EMPTY_ARRAY,
        participants: participants.meta.awarenessLevels[level] || 0,
      }))
    } else throw new Error('2')
  }
)
export const selectSelectedScenariosMap = createSelector(
  selectSelectedScenarios,
  (selectedScenarios) => arrayOfObjectsToObject(selectedScenarios, 'id', true)
)
export const selectSelectedScenariosCount = (state) => selectSelectedScenarios(state).length
export const selectSelectedTemplateSettings = createSelector(
  selectSelectedScenarios,
  getSelectedTemplateSettings
)
export const selectTemplateSettings = createSelector(
  selectSelectedTemplateSettings,
  (state) => state.awarenessCenter.wizardSimulationCampaign.scenariosStep.attackVector,
  selectTypeFilter,
  (selected, manual, typeFilter) => {
    if (selected) return selected
    if (manual) return getSelectedTemplateSettings([manual])
    if (
      TYPE_LABELS[typeFilter] &&
      typeFilter !== TYPE_VALUES.ALL_TYPES &&
      typeFilter !== TYPE_VALUES.ATTACHMENT
    ) {
      return { type: typeFilter, filetype: null, isValid: true }
    }
    return null
  }
)
export const selectAutoSelectDisabledReason = createSelector(
  selectParticipants,
  selectTemplateSettings,
  selectIsTournament,
  selectTypeFilter,
  (participants, settings, isTournament, typeFilter) => {
    if (participants.meta.count <= 2) return 'Only for more than 2 Participants'
    if (isTournament) return null
    if (settings && !settings.isValid) return TEMPLATE_SELECTION_CORRECTION_MESSAGE
    if (!settings && typeFilter === TYPE_VALUES.ATTACHMENT) {
      return 'Choose an attachment format to use auto-select.'
    }
    if (!settings) return 'Choose a template type to use auto-select, or select a template below.'
    return null
  }
)
/**
 * Returning only existing participants levels
 * in following format: { [LEVEL_VALUES.MID_LEVEL]: bool, ... }
 */
export const selectIsLevelsFullyFilled = createSelector(
  selectParticipants,
  selectSelectedScenariosByLevels,
  (participants, selectedScenariosByLevelsArray) => {
    const selectedScenariosByLevels = arrayOfObjectsToObject(
      selectedScenariosByLevelsArray,
      'level'
    )

    return Object.keys(participants.meta.awarenessLevels)
      .filter((level) => participants.meta.awarenessLevels[level])
      .reduce(
        (res, level) => ({
          ...res,
          [level]: selectedScenariosByLevels[level]
            ? selectedScenariosByLevels[level].scenarios.length >=
              selectedScenariosByLevels[level].participants
            : false,
        }),
        {}
      )
  }
)
export const selectPickedCategoryId = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.pickedCategoryId
const selectCategories = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.data.categories
export const selectSortedCategories = createSelector(
  selectCategories,
  selectSelectedDelivery,
  (categories, delivery) =>
    Object.keys(categories)
      .map((categoryId) => ({
        id: categories[categoryId].id,
        name: categories[categoryId].name,
        totalCount: categories[categoryId].count,
        currentCount: categories[categoryId]
          ? categories[categoryId].count
          : categories[categoryId].count,
      }))
      .sort((a, b) => {
        if (a.id === DEFAULT_CATEGORY_ID[delivery]) return -1
        else if (b.id === DEFAULT_CATEGORY_ID[delivery]) return 1
        else return b.count - a.count
      })
)
const getScenarioSelection = (
  s,
  {
    sendingType,
    participants,
    isLevelsFullyFilled,
    selectedScenariosCount,
    selectedTemplateSettings,
    selectedScenariosMap,
    isTournament,
  }
) => {
  const isRandomFullyFilled = selectedScenariosCount >= participants.meta.count
  if (selectedScenariosMap[s.id]) {
    return { ...s, selectionDisabled: false, selectionPopupText: null }
  }

  if (
    !isTournament &&
    selectedTemplateSettings?.isValid &&
    hasSupportedAttachmentFormat(s) &&
    (s.type !== selectedTemplateSettings.type ||
      (s.type === TYPE_VALUES.ATTACHMENT && s.filetype !== selectedTemplateSettings.filetype))
  ) {
    const releaseAction = selectedScenariosCount
      ? 'Clear your selection to change the attack vector.'
      : 'Change the attack vector above.'
    return {
      ...s,
      selectionDisabled: true,
      selectionPopupText: `Selected: ${getTemplateSettingsLabel(
        selectedTemplateSettings
      )}. This template: ${getTemplateSettingsLabel(s)}. ${releaseAction}`,
    }
  }

  let selectionDisabled = false
  let selectionPopupText = null

  if (!participants.meta.count) {
    selectionDisabled = true
    selectionPopupText = null
  } else if (sendingType === SENDING_VALUES.LEVELED) {
    if (isLevelsFullyFilled[s.level] !== undefined) {
      selectionDisabled = isLevelsFullyFilled[s.level]
      selectionPopupText = selectionDisabled
        ? 'You cannot select more Scenarios on this level'
        : null
    } else {
      selectionDisabled = true
      selectionPopupText = 'There are no Participants on this level'
    }
  } else if (sendingType === SENDING_VALUES.RANDOMIZED) {
    selectionDisabled = isRandomFullyFilled
    selectionPopupText = selectionDisabled
      ? 'You cannot select more Scenarios than Participants'
      : null
  } else throw new Error('sendingType is not supported')

  if (!selectionDisabled && !hasSupportedAttachmentFormat(s)) {
    selectionDisabled = true
    selectionPopupText =
      'Invalid attachment settings. Duplicate this template to choose a supported format.'
  }
  if (
    !selectionDisabled &&
    !isTournament &&
    selectedTemplateSettings &&
    !selectedTemplateSettings.isValid
  ) {
    selectionDisabled = true
    selectionPopupText = TEMPLATE_SELECTION_CORRECTION_MESSAGE
  }
  return { ...s, selectionDisabled, selectionPopupText }
}

export const selectScenarios = createSelector(
  selectCategories,
  selectSendingType,
  selectParticipants,
  selectPickedCategoryId,
  selectIsLevelsFullyFilled,
  selectSelectedScenariosCount,
  selectTemplateSettings,
  selectSelectedScenariosMap,
  selectIsTournament,
  (
    categories,
    sendingType,
    participants,
    pickedCategoryId,
    isLevelsFullyFilled,
    selectedScenariosCount,
    selectedTemplateSettings,
    selectedScenariosMap,
    isTournament
  ) => {
    const pickedCategory = categories[pickedCategoryId]
    const relevantScenarios = pickedCategory?.templates || EMPTY_ARRAY
    return relevantScenarios.map((s) =>
      getScenarioSelection(s, {
        sendingType,
        participants,
        isLevelsFullyFilled,
        selectedScenariosCount,
        selectedTemplateSettings,
        selectedScenariosMap,
        isTournament,
      })
    )
  }
)

// IC-50893: a different profile replaces the campaign's selection instead of being blocked by it.
// A mixed (invalid) selection has no locked profile, so it keeps its correction message.
export const getSaveAndAddReplacesSelection = (state, scenario) => {
  const selected = selectSelectedTemplateSettings(state)
  return (
    !selectIsTournament(state) &&
    !!selected?.isValid &&
    (scenario.type !== selected.type ||
      (scenario.type === TYPE_VALUES.ATTACHMENT && scenario.filetype !== selected.filetype))
  )
}

// A duplicate follows the actual campaign selection, not the library browsing filters.
export const getSaveAndAddDisabledReason = (state, scenario) => {
  if (selectSelectedScenariosMap(state)[scenario.id]) return 'This template is already selected.'
  const allowedTypes =
    MAP_SCENARIOS_TYPE_OPTIONS_BY_DELIVERY[selectSelectedDelivery(state)] || EMPTY_ARRAY
  if (
    !allowedTypes.some(({ value }) => value === scenario.type && value !== TYPE_VALUES.ALL_TYPES)
  ) {
    return 'This attack vector is unavailable for the campaign delivery type.'
  }
  if (!getSelectedTemplateSettings([scenario])?.isValid) {
    return 'Choose a valid attack vector and attachment format.'
  }
  const replaces = getSaveAndAddReplacesSelection(state, scenario)
  const participants = selectParticipants(state)
  const selection = getScenarioSelection(scenario, {
    sendingType: selectSendingType(state),
    participants,
    isLevelsFullyFilled: replaces
      ? selectIsLevelsFullyFilled.resultFunc(participants, EMPTY_ARRAY)
      : selectIsLevelsFullyFilled(state),
    selectedScenariosCount: replaces ? 0 : selectSelectedScenariosCount(state),
    selectedTemplateSettings: replaces ? null : selectSelectedTemplateSettings(state),
    selectedScenariosMap: replaces ? EMPTY_OBJECT : selectSelectedScenariosMap(state),
    isTournament: selectIsTournament(state),
  })
  return selection.selectionDisabled
    ? selection.selectionPopupText || 'Add participants before adding a template.'
    : null
}
